import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  makeSyncReduxWithLocalStorageObserver,
  restoreFromLocalStorage,
} from 'app/dataManagement/syncReduxStateWithLocalStorage';
import { MAX_ROTATION_HISTORY_DEPTH } from 'app/utils/rotationHistory';
import observeStore from 'app/utils/redux/observeStore';
import { Store } from 'redux';

export const ROTATION_HISTORY_LOCAL_STORAGE_KEY = 'redux.rotationHistory';

export type RotationMode = 'automatic' | 'fixed' | 'none';
export type FixedRotationAngle = 0 | 90 | 180 | 270;

/**
 * State shape: Record mapping species-agnostic anatomic region keys
 * to arrays of normalized rotation values (up to MAX_ROTATION_HISTORY_DEPTH entries each).
 * Also includes rotation configuration (mode and fixed angle).
 */
export type RotationHistoryState = {
  history: Record<string, number[]>;
  rotationMode: RotationMode;
  fixedRotationAngle: FixedRotationAngle;
};

export type StoreWithRotationHistory = { rotationHistory: RotationHistoryState };

const rotationHistoryInitialState = (): RotationHistoryState => {
  const restored = restoreFromLocalStorage(ROTATION_HISTORY_LOCAL_STORAGE_KEY, {});
  return {
    history: restored?.history ?? restored ?? {},
    rotationMode: restored?.rotationMode ?? 'none',
    fixedRotationAngle: restored?.fixedRotationAngle ?? 0,
  };
};

const rotationHistorySlice = createSlice({
  name: 'rotationHistory',
  initialState: rotationHistoryInitialState,
  reducers: {
    recordRotation(state, action: PayloadAction<{ regionKey: string; rotation: number }>) {
      const { regionKey, rotation } = action.payload;
      if (!state.history[regionKey]) {
        state.history[regionKey] = [];
      }
      state.history[regionKey].push(rotation);
      // Keep only the latest entries
      if (state.history[regionKey].length > MAX_ROTATION_HISTORY_DEPTH) {
        state.history[regionKey] = state.history[regionKey].slice(-MAX_ROTATION_HISTORY_DEPTH);
      }
    },
    setRotationMode(state, action: PayloadAction<{ rotationMode: RotationMode }>) {
      state.rotationMode = action.payload.rotationMode;
    },
    setFixedRotationAngle(
      state,
      action: PayloadAction<{ fixedRotationAngle: FixedRotationAngle }>
    ) {
      state.fixedRotationAngle = action.payload.fixedRotationAngle;
    },
  },
});

// Export actions
export const { recordRotation, setRotationMode, setFixedRotationAngle } =
  rotationHistorySlice.actions;

// Export selectors
export const selectRotationConfiguration = (
  state: StoreWithRotationHistory
): RotationHistoryState => state.rotationHistory;

export const selectRotationHistory = (state: StoreWithRotationHistory): Record<string, number[]> =>
  state.rotationHistory.history;

export const selectRotationMode = (state: StoreWithRotationHistory): RotationMode =>
  state.rotationHistory.rotationMode;

export const selectFixedRotationAngle = (state: StoreWithRotationHistory): FixedRotationAngle =>
  state.rotationHistory.fixedRotationAngle;

// Export localStorage observer
export const attachRotationHistoryWithLocalStorageSynchronizationObserver = (
  store: Store<StoreWithRotationHistory>
) => {
  observeStore(
    store,
    selectRotationConfiguration,
    makeSyncReduxWithLocalStorageObserver(ROTATION_HISTORY_LOCAL_STORAGE_KEY)
  );
};

// Export reducer
const rotationHistoryReducer = rotationHistorySlice.reducer;
export default rotationHistoryReducer;
